<template>
  <footer class="page-footer">
    <div class="top">
      <div class="container container--max-xxwide">
        <NavigationElementFooterMenu />
      </div>
    </div>
    <div class="statement">
      <section class="traditional-statement flex align-center">
        <div class="flags flex">
          <NuxtImg
            alt="Australian Aboriginal Flag"
            height="44"
            loading="lazy"
            src="/img/flag1.png"
            width="66"
          />
          <NuxtImg
            alt="Torres Strait Islander Flag"
            height="44"
            loading="lazy"
            src="/img/flag2.png"
            width="66"
          />
        </div>
        <p class="text-center">
          We acknowledge the Traditional Owners of country throughout Australia
          and recognise their continuing connection to land, waters and culture.
          We pay our respects to their Elders past, present and emerging.
        </p>
      </section>
    </div>
    <div class="statement">
      <section class="inclusivity-statement flex align-center">
        <p class="text-center">
          Compass is committed to ensuring equitable and inclusive responses to
          end elder abuse for people with diverse characteristics and life
          experiences
        </p>
      </section>
    </div>
    <div class="logos">
      <div
        class="container container--max-xxwide flex align-center justify-between"
      >
        <section>
          <NavigationElementBrand />
        </section>
        <section class="partners flex align-center">
          <a
            aria-label="Go to the Attorney-General's Department website - opens in new tab"
            href="https://www.ag.gov.au/"
            rel="external"
            target="_blank"
          >
            <GovernmentLogo
              alt="Attorney-General's Department"
              class="logo__government"
              filled
            />
          </a>
          <a
            aria-label="Go to the Elder Abuse Action Australia website - opens in new tab"
            href="https://eaaa.org.au/"
            rel="external"
            target="_blank"
          >
            <NuxtImg
              alt="Elder Abuse Action Australia"
              loading="lazy"
              src="/img/EAAA.png"
              width="80"
            />
          </a>
        </section>
      </div>
    </div>
    <div class="bottom">
      <div
        class="container container--max-xxwide flex align-center justify-between"
      >
        <section class="legals text-center">
          Made for all Australians regardless of nationality, culture or
          language, Compass is an inclusive website navigating elder abuse.
        </section>
        <section class="copyrights text-center">
          Compass is an initiative of EAAA and funded by the Attorney-General's
          Department Ⓒ&nbsp;Copyright EAAA Compass {{ currentYear }}.
          <a
            href="https://grade.net.au"
            target="_blank"
            rel="external"
            aria-label="Made by Grade - opens in new tab"
            >Made by <strong>Grade</strong></a
          >
        </section>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import GovernmentLogo from '~/assets/images/government_logo.svg'
const currentYear = new Date().getFullYear()
</script>

<style lang="less" scoped>
footer.page-footer {
  background: @color-fill-normal;
  border-top: @2px solid @color-secondary;

  .top,
  .statement {
    border-bottom: @1px solid @color-border;
  }

  .top {
    padding: @gutter-xl @gutter @gutter-lg;

    @media @tablet {
      padding: @gutter-xl @gutter @gutter-xxl;
    }
    @media @desktop {
      padding: @gutter-xl @gutter-xxl @gutter-xxl;
    }
  }

  .statement {
    padding: @gutter-lg @gutter;

    @media @desktop {
      padding: @gutter-lg @gutter-xxl;
    }

    .flags {
      margin: 0 -@gutter-xxs;

      img {
        margin: 0 @gutter-xxs;
      }
    }

    .traditional-statement,
    .inclusivity-statement {
      .margin-auto();
      .flex-col();

      @media @desktop {
        .flex-row();
      }

      p {
        .font-body();
        .text-normal();
        .font-size(12, 18);

        @media @desktop {
          .font-xxs();
        }
      }
    }

    .traditional-statement {
      max-width: @screen-desktop;

      p {
        margin: @gutter-lg 0 0;

        @media @desktop {
          .text-left();

          margin: 0 0 0 @gutter-lg;
        }
      }
    }

    .inclusivity-statement {
      max-width: @screen-tablet;

      p {
        margin: 0 0 @gutter-xxs;
      }
    }
  }

  .logos {
    padding: @gutter-lg @gutter @gutter-sm;

    @media @desktop {
      padding: @gutter-xl @gutter-xxl @gutter-xxs;
    }

    .container {
      .flex-col();

      @media @desktop {
        .flex-row();
      }
    }

    .partners {
      margin: @gutter-xxs -@22px 0;

      @media @desktop {
        margin: 0 -@gutter-lg 0;
      }

      a {
        .block();

        margin: 0 @22px;

        @media @desktop {
          margin: 0 @gutter-lg;
        }
      }
    }
  }

  .logo__government {
    width: 214px;
    height: 46px;
    fill: none;
  }

  .bottom {
    padding: @gutter @gutter-xxl @gutter-xl;

    .container {
      .flex-col();

      @media @desktop {
        .flex-row();
      }
    }

    .legals,
    .copyrights {
      .font-body();
      .font-size(12, 18);
      .text-body();
    }

    .legals {
      max-width: @420px;
      margin-bottom: @gutter-sm;

      @media @desktop {
        .text-left();

        margin-bottom: 0;
      }
    }
    .copyrights {
      max-width: @480px;

      @media @desktop {
        .text-right();
      }
    }
  }
}

body.subscription-is-open footer {
  padding-bottom: calc(var(--subscription-popup-h) + 72px);
}

@media print {
  footer.page-footer {
    border-color: @color-border;

    .top,
    .statement,
    .brand {
      .hidden();
    }
  }
}
</style>
